import type { Customer, Member } from "@payezy/api-client/admin";
import { getCustomerOptions, startViewAsMutation } from "@payezy/api-client/admin/query";
import {
  Alert, Badge, Button, Card, CardBody, CardHeader, DescriptionList, Dialog, EmptyState, Field, formatDate, formatUSD, humanize,
  Menu, MenuItem, MenuLabel, MenuSeparator, PageHeader, Select, shortId, Stat, Table, Tabs, TD, Textarea, TH, THead, TR, toast,
} from "@payezy/ui";
import { useMutation, useSuspenseQuery } from "@tanstack/react-query";
import { Link, useParams } from "@tanstack/react-router";
import { Eye, MoreHorizontal } from "lucide-react";
import { useState, type ReactNode } from "react";
import { ActionDialog } from "../components/ActionDialog";
import { showError } from "../lib/notify";
import { actionTone, breakKindLabel, onboardingTone, orgStatusTone, paymentTone, useStaff } from "../lib/staff";

export function CustomerPage() {
  const { orgId } = useParams({ from: "/app/customers/$orgId" });
  const c = useSuspenseQuery(getCustomerOptions({ path: { orgId } })).data;
  const me = useStaff();
  const [tab, setTab] = useState("overview");
  const o = c.org;
  const total = c.accounts.reduce((s, a) => s + Number(a.available), 0);
  return (
    <>
      <div className="mb-1 text-sm text-ink-500"><Link to="/customers" className="hover:underline">Customers</Link> / {shortId(o.id)}</div>
      <PageHeader
        title={<span className="flex items-center gap-3">{o.name}<Badge tone={orgStatusTone[o.status]}>{humanize(o.status)}</Badge></span>}
        description={`${humanize(o.type)} account · opened ${formatDate(o.created_at)}${o.status_reason ? ` · ${o.status_reason}` : ""}`}
        actions={
          <div className="flex gap-2">
            {me.can("impersonate:read") && <ViewAsDialog orgId={o.id} members={c.members} trigger={<Button variant="secondary"><Eye className="size-4" /> View as</Button>} />}
            <OrgActions c={c} />
          </div>
        }
      />
      <Tabs
        value={tab}
        onValueChange={setTab}
        tabs={[
          { value: "overview", label: "Overview" },
          { value: "members", label: `Members (${c.members.length})` },
          { value: "recipients", label: `Recipients (${c.recipients.length})` },
          { value: "payments", label: "Payments" },
          { value: "staff", label: "Staff activity" },
        ]}
      />
      {tab === "overview" && (
        <div className="grid gap-6 lg:grid-cols-3">
          <Card className="lg:col-span-2">
            <CardHeader title="Accounts" description="Ledger balances (USD)." />
            <CardBody className="grid grid-cols-3 gap-4 border-b border-ink-100">
              <Stat label="Available" value={formatUSD(total)} />
              <Stat label="Single payout limit" value={formatUSD(o.single_payout_limit)} />
              <Stat label="Daily payout limit" value={formatUSD(o.daily_payout_limit)} />
            </CardBody>
            <Table>
              <THead><TR><TH>Account</TH><TH>Status</TH><TH className="text-right">Posted</TH><TH className="text-right">Pending in</TH><TH className="text-right">Pending out</TH><TH className="text-right">Available</TH></TR></THead>
              <tbody>
                {c.accounts.map((a) => (
                  <TR key={a.id}>
                    <TD className="font-medium">{a.name}</TD>
                    <TD>{a.status}</TD>
                    <TD className="text-right tabular-nums">{formatUSD(a.posted)}</TD>
                    <TD className="text-right tabular-nums">{formatUSD(a.pending_in)}</TD>
                    <TD className="text-right tabular-nums">{formatUSD(a.pending_out)}</TD>
                    <TD className="text-right font-medium tabular-nums">{formatUSD(a.available)}</TD>
                  </TR>
                ))}
              </tbody>
            </Table>
            {c.accounts.length === 0 && <EmptyState title="No accounts yet" description="Accounts open when verification is approved." />}
          </Card>
          <Card>
            <CardHeader
              title="Verification"
              actions={c.onboarding && me.can("onboarding:review") && (
                <ActionDialog type="onboarding.resync" orgId={o.id} trigger={<Button size="sm" variant="secondary">Re-sync</Button>} />
              )}
            />
            <CardBody>
              {c.onboarding ? (
                <>
                  <DescriptionList
                    items={[
                      { label: "Status", value: <Badge tone={onboardingTone[c.onboarding.status]}>{humanize(c.onboarding.status)}</Badge> },
                      { label: "Entity", value: humanize(c.onboarding.entity_type) },
                      { label: "Legal name", value: c.onboarding.display_name || "—" },
                      { label: "Tax id", value: c.onboarding.tax_id_last4 ? `•••• ${c.onboarding.tax_id_last4}` : "—", mono: true },
                      { label: "State", value: c.onboarding.state || "—" },
                      { label: "Partner", value: c.onboarding.partner || "—" },
                      { label: "Submitted", value: formatDate(c.onboarding.submitted_at, true) },
                    ]}
                  />
                  {c.onboarding.decision_reason && <Alert className="mt-3" tone="warning">{c.onboarding.decision_reason}</Alert>}
                  {c.onboarding.requested.length > 0 && (
                    <div className="mt-4">
                      <p className="mb-1 text-xs font-medium uppercase tracking-wide text-ink-500">Requested documents</p>
                      <ul className="text-sm">{c.onboarding.requested.map((d, i) => <li key={i}>{d.display_name}{d.person_label ? ` (${d.person_label})` : ""} — {humanize(d.status)}</li>)}</ul>
                    </div>
                  )}
                  {c.onboarding.documents.length > 0 && (
                    <div className="mt-4">
                      <p className="mb-1 text-xs font-medium uppercase tracking-wide text-ink-500">Uploaded</p>
                      <ul className="text-sm">{c.onboarding.documents.map((d) => <li key={d.id}>{humanize(d.kind)} · {d.file_name} — {d.status}</li>)}</ul>
                    </div>
                  )}
                </>
              ) : (
                <p className="text-sm text-ink-500">No application yet.</p>
              )}
            </CardBody>
          </Card>
        </div>
      )}
      {tab === "members" && <MembersTab c={c} />}
      {tab === "recipients" && (
        <Card>
          <CardHeader
            title="Recipients"
            description="Account numbers are encrypted; only the last four digits are shown."
            actions={me.can("onboarding:review") && c.recipients.length > 0 && (
              <ActionDialog type="counterparty.set_screening" orgId={o.id} recipients={c.recipients} trigger={<Button size="sm" variant="secondary">Screening decision</Button>} />
            )}
          />
          <Table>
            <THead><TR><TH>Name</TH><TH>Kind</TH><TH>Screening</TH><TH>Accounts</TH></TR></THead>
            <tbody>
              {c.recipients.map((r) => (
                <TR key={r.id}>
                  <TD className="font-medium">{r.name}</TD>
                  <TD>{humanize(r.kind)}</TD>
                  <TD><Badge tone={r.screening === "clear" ? "success" : r.screening === "blocked" ? "danger" : "warning"}>{r.screening}</Badge></TD>
                  <TD className="text-sm">
                    {r.accounts.map((a) => (
                      <div key={a.id}>{a.type === "bank" ? `${a.bank_name ?? "Bank"} •••• ${a.account_last4 ?? ""}` : `${a.network} ${a.address?.slice(0, 8)}…${a.address?.slice(-6)}`}</div>
                    ))}
                  </TD>
                </TR>
              ))}
            </tbody>
          </Table>
          {c.recipients.length === 0 && <EmptyState title="No recipients" />}
        </Card>
      )}
      {tab === "payments" && (
        <Card>
          <CardHeader title="Recent payments" actions={<Link to="/payments" search={{ org_id: o.id }}><Button size="sm" variant="secondary">All payments</Button></Link>} />
          <Table>
            <THead><TR><TH>Date</TH><TH>Kind</TH><TH>Recipient</TH><TH>Status</TH><TH className="text-right">Amount</TH></TR></THead>
            <tbody>
              {c.payments.map((p) => (
                <TR key={p.id} className="hover:bg-ink-50">
                  <TD><Link to="/payments/$paymentId" params={{ paymentId: p.id }} className="hover:underline">{formatDate(p.created_at, true)}</Link></TD>
                  <TD>{humanize(p.kind)} · {p.rail}</TD>
                  <TD>{p.recipient ?? "—"}</TD>
                  <TD><Badge tone={paymentTone(p.status)}>{humanize(p.status)}</Badge></TD>
                  <TD className="text-right tabular-nums">{p.direction === "outbound" ? "−" : "+"}{formatUSD(p.amount)}</TD>
                </TR>
              ))}
            </tbody>
          </Table>
          {c.payments.length === 0 && <EmptyState title="No payments" />}
        </Card>
      )}
      {tab === "staff" && (
        <div className="grid gap-6">
          <Card>
            <CardHeader title="Staff actions" />
            <Table>
              <THead><TR><TH>Requested</TH><TH>Action</TH><TH>By</TH><TH>Reason</TH><TH>Status</TH></TR></THead>
              <tbody>
                {c.actions.map((a) => (
                  <TR key={a.id}>
                    <TD>{formatDate(a.created_at, true)}</TD>
                    <TD>{a.title ?? a.type}</TD>
                    <TD>{a.requested_by_email}</TD>
                    <TD className="max-w-80 truncate" title={a.reason}>{a.reason}</TD>
                    <TD><Badge tone={actionTone[a.status]}>{humanize(a.status)}</Badge></TD>
                  </TR>
                ))}
              </tbody>
            </Table>
            {c.actions.length === 0 && <EmptyState title="No staff actions" />}
          </Card>
          <Card>
            <CardHeader title="View-as sessions" />
            <Table>
              <THead><TR><TH>Started</TH><TH>Staff</TH><TH>Scope</TH><TH>Reason</TH><TH>Status</TH></TR></THead>
              <tbody>
                {c.view_as.map((v) => (
                  <TR key={v.id}>
                    <TD>{formatDate(v.created_at, true)}</TD>
                    <TD>{v.staff_email ?? shortId(v.staff_id)}</TD>
                    <TD><Badge tone={v.scope === "write" ? "danger" : "info"}>{v.scope}</Badge></TD>
                    <TD className="max-w-80 truncate" title={v.reason}>{v.reason}</TD>
                    <TD>{v.status}</TD>
                  </TR>
                ))}
              </tbody>
            </Table>
            {c.view_as.length === 0 && <EmptyState title="No view-as sessions" />}
          </Card>
          {me.can("recon:manage") && (
            <Card>
              <CardHeader title="Reconciliation breaks" />
              <Table>
                <THead><TR><TH>Detected</TH><TH>Kind</TH><TH>Reference</TH><TH>Expected → actual</TH><TH>Status</TH></TR></THead>
                <tbody>
                  {c.breaks.map((b) => (
                    <TR key={b.id}>
                      <TD>{formatDate(b.created_at, true)}</TD>
                      <TD>{breakKindLabel[b.kind] ?? b.kind}</TD>
                      <TD className="font-mono text-xs">{b.reference}</TD>
                      <TD className="text-sm">{b.expected} → {b.actual}</TD>
                      <TD>{b.status}</TD>
                    </TR>
                  ))}
                </tbody>
              </Table>
              {c.breaks.length === 0 && <EmptyState title="No breaks" />}
            </Card>
          )}
        </div>
      )}
    </>
  );
}

type Pending = { type: string; userId?: string; defaults?: Record<string, string> } | null;

function OrgActions({ c }: { c: Customer }) {
  const me = useStaff();
  const [pending, setPending] = useState<Pending>(null);
  if (!me.can("orgs:restrict")) return null;
  const o = c.org;
  const item = (type: string, label: string, defaults?: Record<string, string>) => (
    <MenuItem onSelect={() => setPending({ type, defaults })}>{label}</MenuItem>
  );
  return (
    <>
      <Menu trigger={<Button variant="secondary" size="icon" aria-label="More actions"><MoreHorizontal className="size-4" /></Button>}>
        <MenuLabel>Controls</MenuLabel>
        {o.status !== "restricted" && o.status !== "closed" && item("org.set_status", "Restrict outgoing payments", { status: "restricted" })}
        {o.status !== "suspended" && o.status !== "closed" && item("org.set_status", "Suspend", { status: "suspended" })}
        {(o.status === "restricted" || o.status === "suspended") && item("org.set_status", "Reactivate (needs approval)", { status: "active" })}
        {item("org.set_limits", "Change payout limits", { single: o.single_payout_limit, daily: o.daily_payout_limit })}
        <MenuSeparator />
        {o.status !== "closed" && item("org.set_status", "Close account (needs approval)", { status: "closed" })}
      </Menu>
      {pending && (
        <ActionDialog key={pending.type + JSON.stringify(pending.defaults)} type={pending.type} orgId={o.id} defaults={pending.defaults} open onOpenChange={(v) => !v && setPending(null)} />
      )}
    </>
  );
}

function MembersTab({ c }: { c: Customer }) {
  const me = useStaff();
  const [pending, setPending] = useState<Pending>(null);
  const [viewAs, setViewAs] = useState<Member | null>(null);
  const menu = (m: Member) => {
    const item = (type: string, label: ReactNode, defaults?: Record<string, string>) => (
      <MenuItem onSelect={() => setPending({ type, userId: m.user_id, defaults })}>{label}</MenuItem>
    );
    return (
      <Menu trigger={<Button variant="ghost" size="icon" aria-label="Member actions"><MoreHorizontal className="size-4" /></Button>}>
        <MenuLabel>{m.email}</MenuLabel>
        {me.can("impersonate:read") && <MenuItem onSelect={() => setViewAs(m)}>View as (read-only)</MenuItem>}
        {me.can("impersonate:write") && item("impersonation.write", "Request write access")}
        {me.can("customers:view") && (
          <>
            <MenuSeparator />
            {item("user.send_recovery", "Email recovery link")}
            {item("user.reset_mfa", "Reset MFA (needs approval)")}
            {item("user.set_status", "Suspend user", { status: "suspended" })}
            {item("user.set_status", "Reactivate user (needs approval)", { status: "active" })}
          </>
        )}
      </Menu>
    );
  };
  return (
    <Card>
      <Table>
        <THead><TR><TH>Name</TH><TH>Email</TH><TH>Role</TH><TH>Status</TH><TH>Joined</TH><TH /></TR></THead>
        <tbody>
          {c.members.map((m) => (
            <TR key={m.user_id}>
              <TD className="font-medium">{m.name || "—"}</TD>
              <TD>{m.email}</TD>
              <TD>{m.role}</TD>
              <TD><Badge tone={m.status === "active" ? "success" : "neutral"}>{m.status}</Badge></TD>
              <TD>{formatDate(m.joined_at)}</TD>
              <TD className="text-right">{menu(m)}</TD>
            </TR>
          ))}
        </tbody>
      </Table>
      {pending && (
        <ActionDialog
          key={pending.type + pending.userId + JSON.stringify(pending.defaults)}
          type={pending.type}
          orgId={c.org.id}
          userId={pending.userId}
          defaults={pending.defaults}
          open
          onOpenChange={(v) => !v && setPending(null)}
        />
      )}
      {viewAs && <ViewAsDialog orgId={c.org.id} members={[viewAs]} userId={viewAs.user_id} open onOpenChange={(v) => !v && setViewAs(null)} />}
    </Card>
  );
}

/** Opens a read-only view-as session in a new tab; the token never touches the admin app's storage. */
export function ViewAsDialog({ orgId, members, userId, trigger, open: controlled, onOpenChange }: {
  orgId: string;
  members: Member[];
  userId?: string;
  trigger?: ReactNode;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}) {
  const [uncontrolled, setUncontrolled] = useState(false);
  const open = controlled ?? uncontrolled;
  const setOpen = (o: boolean) => (onOpenChange ?? setUncontrolled)(o);
  const [user, setUser] = useState(userId ?? members.find((m) => m.status === "active")?.user_id ?? "");
  const [reason, setReason] = useState("");
  const start = useMutation({
    ...startViewAsMutation(),
    onSuccess: (g) => {
      setOpen(false);
      setReason("");
      window.open(g.url, "_blank", "noopener,noreferrer");
      toast.success("View-as session opened in a new tab.");
    },
    onError: showError,
  });
  return (
    <Dialog
      open={open}
      onOpenChange={setOpen}
      trigger={trigger}
      title="View as customer"
      description="Opens the customer app read-only as this person for up to 30 minutes. The session is recorded in the audit log with your reason, and the customer app shows a staff banner throughout."
      footer={
        <>
          <Button variant="secondary" onClick={() => setOpen(false)}>Cancel</Button>
          <Button loading={start.isPending} disabled={!user || reason.trim().length < 10} onClick={() => start.mutate({ body: { org_id: orgId, user_id: user, reason } })}>
            Start session
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        {!userId && (
          <Field label="View as">
            <Select value={user} onChange={(e) => setUser(e.target.value)}>
              {members.filter((m) => m.status === "active").map((m) => <option key={m.user_id} value={m.user_id}>{m.name || m.email} ({m.role})</option>)}
            </Select>
          </Field>
        )}
        <Field label="Reason" hint="Ticket number and what you need to see.">
          <Textarea value={reason} onChange={(e) => setReason(e.target.value)} placeholder="SUP-1234: customer can't find their deposit instructions" />
        </Field>
      </div>
    </Dialog>
  );
}
